import {
  UnitTransferDocumentFulfilmentModeEnumType,
  UnitTransferDocumentStatusEnumType,
  UnitTransferSignatureStatusEnumType,
} from '__generated__/graphql';

/**
 * Ops previously had to open the Documents tab to learn whether checks were outstanding and the
 * Signatures tab to learn who had not signed. These are the numbers that decide whether this
 * request is actually blocked, derived once so the Overview can state them up front.
 */

type BlockerDocument = {
  status: UnitTransferDocumentStatusEnumType;
  fulfilmentMode: UnitTransferDocumentFulfilmentModeEnumType;
  isRequired: boolean;
  checks: { severity: string; humanVerdict?: unknown }[];
};

type BlockerSignatureRequest = {
  status: UnitTransferSignatureStatusEnumType;
};

export type UnitTransferBlockers = {
  checksAwaitingReview: number;
  signaturesSigned: number;
  signaturesTotal: number;
};

const BLOCK_SEVERITY = 'BLOCK';

export default function deriveUnitTransferBlockers({
  documents,
  signatureRequests,
}: {
  documents: BlockerDocument[];
  signatureRequests: BlockerSignatureRequest[];
}): UnitTransferBlockers {
  // Mirrors assertDocumentsReadyForCompletion (BUS_067) exactly: only a BLOCK-severity check
  // with no human verdict, on a required, non-signature document that is already APPROVED, is
  // what actually stops `markUnitTransferDocumentsComplete`. Counting anything broader would
  // show ops a blocker that does not exist.
  const gatedDocuments = documents.filter(
    document =>
      document.isRequired &&
      document.fulfilmentMode !== UnitTransferDocumentFulfilmentModeEnumType.Signature &&
      document.status === UnitTransferDocumentStatusEnumType.Approved
  );

  const checksAwaitingReview = gatedDocuments.reduce(
    (count, document) =>
      count +
      document.checks.filter(
        check =>
          check.severity === BLOCK_SEVERITY &&
          (check.humanVerdict === null || check.humanVerdict === undefined)
      ).length,
    0
  );

  const signaturesSigned = signatureRequests.filter(
    signatureRequest => signatureRequest.status === UnitTransferSignatureStatusEnumType.Signed
  ).length;

  return {
    checksAwaitingReview,
    signaturesSigned,
    signaturesTotal: signatureRequests.length,
  };
}
